import { useLocation, useNavigate } from "react-router-dom";
import { LocaleLink as Link, useLanguageSwitcher } from "@/components/landing/LocaleLink";
import { stripLocale, localizedPath, localeFromPath } from "@/lib/locale";
import { useTranslation } from "react-i18next";
// Isotipo oficial (`src/assets/playium_mark/`, read-only). Mismo motivo que
// Header.tsx: el isotipo es neutro y el wordmark se compone en HTML para
// que el tagline pueda traducirse. Usamos la versión full-res 2671×2650
// (~73 KB) para que no pixele en retina; al ser el mismo asset que el
// Header, Vite lo deduplica en el build (un único hash compartido).
import playiumIsotype from "@/assets/playium_mark/Logo_Playium-01.webp";
import { type LandingLang } from "@/lib/i18n-landing";
import { usePlatformUserCount } from "@/hooks/use-platform-user-count";
import { MARKETING_PAGES } from "./flags";
import { trackEvent } from "@/lib/analytics";

interface FooterProps {
  /**
   * Smooth-scroll en el hub. Si NO se pasa (Footer en una spoke), los
   * anchors del bloque "Producto" navegan al hub con `/#features` y el
   * navegador hace el scroll al llegar.
   */
  onScrollTo?: (id: string) => void;
}

// Anchors internos del hub. `pricing` salió de aquí al mover los packs a su
// propia spoke: el ancla apuntaba a una sección que ya no existe y no hacía
// nada al pulsarla.
const HUB_ANCHORS: Array<{ id: string; key: "features" }> = [
  { id: "features", key: "features" },
];

// Rutas de spokes (Stream C). Slugs en inglés desde Bloque 1 v2 — los slugs
// ES viejos viven sólo como redirects en App.tsx + vercel.json.
const SPOKE_LINKS: Array<{ to: string; key: "pricing" | "forChains" | "contact" | "about" }> = [
  { to: "/pricing", key: "pricing" },
  { to: "/chain", key: "forChains" },
  { to: "/contact", key: "contact" },
  { to: "/about-us", key: "about" },
];

// /chain solo se enlaza cuando está publicada (ver flags.ts).
const VISIBLE_SPOKE_LINKS = SPOKE_LINKS.filter(
  (l) => l.key !== "forChains" || MARKETING_PAGES.chain,
);

const LEGAL_LINKS: Array<{
  to: string;
  key: "legalNotice" | "privacy" | "terms" | "cookies" | "refunds" | "dataDeletion" | "allLegal";
}> = [
  { to: "/legal-notice", key: "legalNotice" },
  { to: "/privacy", key: "privacy" },
  { to: "/terms", key: "terms" },
  { to: "/cookies", key: "cookies" },
  { to: "/refunds", key: "refunds" },
  // LDT-700: /data-deletion no tenía enlace en el pie (sólo se llegaba desde
  // la config de Meta), y /legal es la portada de todos los textos.
  { to: "/data-deletion", key: "dataDeletion" },
  { to: "/legal", key: "allLegal" },
];

const LANGS: LandingLang[] = ["es", "ca", "en"];

export default function Footer({ onScrollTo }: FooterProps) {
  const { t, i18n } = useTranslation();
  const userCount = usePlatformUserCount();
  const navigate = useNavigate();
  const location = useLocation();
  const switchLang = useLanguageSwitcher();
  const year = new Date().getFullYear();
  const isHub = stripLocale(location.pathname) === "/";
  const currentLang = (i18n.language as LandingLang) || "es";

  const handleAnchor = (id: string) => {
    if (isHub && onScrollTo) onScrollTo(id);
    else navigate(localizedPath(`/#${id}`, localeFromPath(location.pathname)));
  };

  return (
    <footer className="border-t border-playium-navy/10 bg-playium-neutral-2/30 px-4 sm:px-6 py-14">
      <div className="max-w-6xl mx-auto">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4 mb-10">
          {/* Brand + tagline */}
          <div className="space-y-3">
            <div className="flex items-center gap-2.5">
              <img loading="lazy"
                src={playiumIsotype}
                alt=""
                aria-hidden="true"
                className="h-8 w-8 shrink-0"
              />
              <span className="text-base font-bold tracking-wide text-[#092348]">
                Playium
              </span>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed max-w-xs">
              {t("nav.tagline")}
            </p>
          </div>

          {/* Producto */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-playium-navy mb-3">
              {t("footer.product")}
            </p>
            <ul className="space-y-2">
              {HUB_ANCHORS.map((l) => (
                <li key={l.id}>
                  <button
                    onClick={() => handleAnchor(l.id)}
                    className="text-sm text-gray-600 hover:text-playium-navy transition-colors"
                  >
                    {t(`footer.links.${l.key}`)}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Empresa */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-playium-navy mb-3">
              {t("footer.company")}
            </p>
            <ul className="space-y-2">
              {VISIBLE_SPOKE_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-sm text-gray-600 hover:text-playium-navy transition-colors"
                  >
                    {t(`footer.links.${l.key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-playium-navy mb-3">
              {t("footer.legal")}
            </p>
            <ul className="space-y-2">
              {LEGAL_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-sm text-gray-600 hover:text-playium-navy transition-colors"
                  >
                    {t(`footer.links.${l.key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="pt-6 border-t border-gray-200 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between text-xs text-gray-500">
          <span>© {year} Playium. {t("footer.rights")}</span>

          {/* Selector idioma — vive en el footer desde Bloque 2 (movido fuera
              del Header para no saturar el navbar dinámico). Sigue accesible
              en la app móvil porque el Sheet del Header también lo monta. */}
          <div
            role="group"
            aria-label={t("nav.language")}
            className="flex items-center gap-1 bg-gray-100 rounded-full p-0.5 self-start sm:self-auto"
          >
            {LANGS.map((l) => (
              <button
                key={l}
                onClick={() => {
                  if (l !== currentLang) {
                    trackEvent("language_switched", { from: currentLang, to: l });
                  }
                  switchLang(l);
                }}
                aria-pressed={currentLang === l}
                aria-label={l.toUpperCase()}
                className={`px-2.5 py-1 text-[11px] font-semibold rounded-full transition-all ${
                  currentLang === l
                    ? "bg-white shadow-sm text-playium-navy"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>

          {userCount !== null && (
            <span className="text-gray-400 sm:order-last">
              {t("hero.trust", { users: userCount.toLocaleString(i18n.language) })}
            </span>
          )}
        </div>
      </div>
    </footer>
  );
}
